import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';

import { getDocumentName, getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType, GenericStatusId } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { Admin as AdminCmp } from '../../components/Admin';
import {
  PATH_RESOLVABLE_MISSION_TYPE_ADMIN,
  PATH_RESOLVABLE_MISSION_TYPE_ADMIN_EDIT,
} from '../../app/constants/paths.constant';
import { GenericAdminForm } from '../../forms/GenericAdmin';
import { memoForm2ApiMapper } from '../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { useGraphQLMutation } from '../../hooks/useGraphQLMutation.hook';
import { useMemos } from '../../forms/MemoBeforeSubmit/hooks/useMemos.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { ResolvableMissionTypeListCmp_AllResolvableMissionTypes_QueryDocument } from '../../components/ResolvableMissionTypeList/hooks/useFindAllResolvableMissionTypesQuery.hook';
import type { GenericAdminFormValues } from '../../forms/GenericAdmin';

import { ResolvableMissionTypePage_OneResolvableMissionType_QueryDocument } from './hooks/useFindOneResolvableMissionTypeQuery.hook';

export const ResolvableMissionTypePage_Admin_ResolvableMissionType_Fragment =
  graphql(`
    fragment ResolvableMissionTypePage_Admin_ResolvableMissionType_Fragment on ResolvableMissionType {
      _id
      statusId
      createdAt
      creatorId
      updatedAt
      updaterId
    }
  `);

export const ResolvableMissionTypeAdmin_ResolvableMissionType_Foapu_Mutation =
  graphql(`
    mutation ResolvableMissionTypeAdmin_ResolvableMissionType_Foapu(
      $filter: IdFilterInput!
      $update: ResolvableMissionTypePartialInput!
      $versionData: VersionDataInput
    ) {
      findOneAndPartialUpdateResolvableMissionType(
        filter: $filter
        update: $update
        versionData: $versionData
      ) {
        _id
      }
    }
  `);

interface Props {
  readonly resolvableMissionType: FragmentType<
    typeof ResolvableMissionTypePage_Admin_ResolvableMissionType_Fragment
  >;
}

export const Admin = ({
  resolvableMissionType: pResolvableMissionType,
}: Readonly<Props>) => {
  const resolvableMissionType = getFragmentData(
    ResolvableMissionTypePage_Admin_ResolvableMissionType_Fragment,
    pResolvableMissionType,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_MISSION_TYPE_ADMIN,
      edit: PATH_RESOLVABLE_MISSION_TYPE_ADMIN_EDIT,
    },
    { id: resolvableMissionType._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { enqueueSnackbar } = useSnackbar();

  const { clearMemos, memos, setMemos } = useMemos();

  // mutations
  const { mutate: foapuResolvableMissionTypeMutation } = useGraphQLMutation(
    ResolvableMissionTypeAdmin_ResolvableMissionType_Foapu_Mutation,
  );

  // on success
  const queryClient = useQueryClient();

  const mutate = useCallback(
    (
      values: GenericAdminFormValues,
      handleError: (error: unknown) => unknown,
      handleSuccess: () => unknown,
    ) => {
      const onSuccess = () => {
        leaveEditionMode();
        clearMemos();
        enqueueSnackbar('Modification enregistrée.');
        handleSuccess();
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(
              ResolvableMissionTypePage_OneResolvableMissionType_QueryDocument,
            ),
            { id: resolvableMissionType._id },
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(
              ResolvableMissionTypeListCmp_AllResolvableMissionTypes_QueryDocument,
            ),
          ],
        });
      };

      foapuResolvableMissionTypeMutation(
        {
          filter: {
            _id: resolvableMissionType._id,
          },
          update: values,
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError: handleError,
          onSuccess,
        },
      );
    },
    [
      clearMemos,
      enqueueSnackbar,
      foapuResolvableMissionTypeMutation,
      leaveEditionMode,
      memos,
      resolvableMissionType._id,
      queryClient,
    ],
  );

  return (
    <>
      <AdminCmp
        createdAt={resolvableMissionType.createdAt}
        creatorId={resolvableMissionType.creatorId}
        id={resolvableMissionType._id}
        statusId={resolvableMissionType.statusId}
        updatedAt={resolvableMissionType.updatedAt}
        updaterId={resolvableMissionType.updaterId}
      />
      <SpeedDial
        actions={[
          {
            id: 'resolvableMissionTypeAdmin',
            icon: <DisplaySettingsIcon />,
            name: 'Administrer',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur l'Administration du Type de Mission"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <GenericAdminForm
          clearMemos={clearMemos}
          memos={memos}
          onCancel={leaveEditionMode}
          onSubmit={mutate}
          setMemos={setMemos}
          statusId={resolvableMissionType.statusId as GenericStatusId}
        />
      </RightDrawer>
    </>
  );
};
